/* eslint-disable */
import React, { useState } from "react";
import Axios from "axios";

import { MEDIA_URL, VIEW_DNA_URL } from "constants/Common";
import UtilService from "services/util";
import { isNotEmpty } from "util/utils";

import { IncreasePriceAPIs } from "constants/apiConstant";

import Loader from "../Loader";
import InputNumber from "../InputNumber/InputNumber";
import OpenNotification from "../CommonButton/OpenNotification";
import CommonModal from "../CommonModal";

import "./increasePrice.less";

const generateApiRequest = (t) => ({
	url: [
		{ origUrl: `${MEDIA_URL}/B2B/imaged/${t.vStnId}/still.jpg`, ref: "image", vStnId: t.vStnId },
		{ origUrl: `${MEDIA_URL}/B2B/imaged/${t.vStnId}/video.mp4`, ref: "video", vStnId: t.vStnId },
		{ origUrl: `${MEDIA_URL}/CERTPDF/${t.rptNo}.pdf`, ref: "cert", vStnId: t.vStnId },
		{ origUrl: `${VIEW_DNA_URL}/dna/${t.vStnId}`, ref: "dna", vStnId: t.vStnId },
	],
});

const fetchWhatsAppContents = async (t, setLoader, setClipBoardRes) => {
	setLoader(true);
	const apiReq = generateApiRequest(t);

	await Axios
		.post("https://tiny.diadna.com/api/shortarr", apiReq)
		.then((res) => {
			setClipBoardRes((prev) => [...prev, ...res?.data?.data]);
			setLoader(false);
		})
		.catch((error) => {
			OpenNotification({
				type: "error",
				title: error?.message || "Something went wrong on the server. Please try again.",
			});
			setLoader(false);
		});
};

const useGenerateClipboardArray = (clipBoardRes, clipArray, setClipArray) => {
	React.useEffect(() => {
		const newClipboardObject = clipBoardRes.reduce((acc, item) => {
			acc[item.ref] = item.shortUrl;
			acc.vStnId = item.vStnId;
			return acc;
		}, {});

		if (isNotEmpty(newClipboardObject) && !clipArray.some((obj) => obj.vStnId === newClipboardObject.vStnId)) {
			setClipArray([...clipArray, newClipboardObject]);
		}
	}, [clipBoardRes, clipArray, setClipArray]);
};

const useGenerateCopyContent = (clipArray, checked, finalCalc) => {
	const [clipboardGenerated, setClipboardGenerated] = React.useState(false);

	React.useEffect(() => {
		setClipboardGenerated(false);
	}, [clipArray, checked, finalCalc]);

	// Memoize the clipboard generation logic
	const generateClipboardContent = React.useCallback(() => {
		if (!clipArray.length || !checked.length) return "";

		const sStsAvailableStages = ['A', 'L', 'R', 'S', 'Z', 'C', 'G', 'V', 'E', 'T', 'Y'];
		const checkedVStnIds = checked.map((item) => item.vStnId);
		const filteredClipArray = clipArray.filter((item) => checkedVStnIds.includes(item.vStnId));
		const uniqueClips = Array.from(new Set(filteredClipArray.map(JSON.stringify))).map(JSON.parse);

		return uniqueClips
			.map((clip) => {
				const checkedItem = checked.find((item) => item.vStnId === clip.vStnId);
				const finalCalcItem = finalCalc.find((item) => item.vStnId === clip.vStnId);
				const sStsItem = checkedItem?.sSts ?? "Unknown";
				const stage = sStsAvailableStages.includes(sStsItem) ? "Available" : "Memo";

				const details = [
					`Stock# ${clip.vStnId}`,
					`Stage: ${stage}`,
					`Image: ${clip.image}`,
					`Video: ${clip.video}`,
					`Cert: ${clip.cert}`,
					`DNA: ${clip.dna}`,
					[
						checkedItem?.shpNm,
						checkedItem?.colNm,
						checkedItem?.clrNm,
						checkedItem?.cutNm,
						checkedItem?.polNm,
						checkedItem?.symNm,
						checkedItem?.fluNm,
						checkedItem?.lsrInc,
					]
						.filter(Boolean)
						.join(" - "),
					[
						checkedItem?.crt && `${checkedItem.crt} Ct`,
						finalCalcItem?.finalDiscPercent && `${finalCalcItem.finalDiscPercent}% Off`,
						finalCalcItem?.finalRate && `P/Ct: $${finalCalcItem.finalRate}`,
						finalCalcItem?.finalValue && `Total: $${finalCalcItem.finalValue}`,
					]
						.filter(Boolean)
						.join(" "),
				]
					.filter(Boolean)
					.join("\n");

				return details;
			})
			.join("\n\n");
	}, [clipArray, checked, finalCalc]);

	// Use useEffect to trigger clipboard logic only when necessary
	React.useEffect(() => {
		if (clipboardGenerated) return;

		const clipboardContent = generateClipboardContent();
		if (!clipboardContent) return;

		navigator.clipboard.writeText(clipboardContent)
			.then(() => setClipboardGenerated(true))
			.catch((err) => {
				if (document.hasFocus()) {
					OpenNotification({
						type: "error",
						title: err?.message || "Clipboard operation failed.",
					});
				} else {
					OpenNotification({
						type: "warning",
						title: "Cannot copy to clipboard as the document is not in focus.",
					});
				}
			});
	}, [generateClipboardContent]);
};

function IncreasePrice({ checked, handleOk, onClose }) {
	const [clipBoardRes, setClipBoardRes] = useState([]);
	const [clipArray, setClipArray] = useState([]);
	const [loading, setLoading] = useState(false);
	const [markupRap, setMarkupRap] = useState("");
	const [markupPerPriceCt, setMarkupPerPriceCt] = useState("");
	const [markupDolPriceCt, setMarkupDolPriceCt] = useState("");
	const [markupPerValue, setMarkupPerValue] = useState("");
	const [markupRapDes, setMarkupRapDes] = useState();
	const [markupPerPriceCtDes, setMarkupPerPriceCtDes] = useState();
	const [markupDolPriceCtDes, setMarkupDolPriceCtDes] = useState();
	const [markupPerValueDes, setMarkupPerValueDes] = useState();
	const [finalCalc, setFinalCalc] = useState([]);

	useGenerateClipboardArray(clipBoardRes, clipArray, setClipArray);
	useGenerateCopyContent(clipArray, checked, finalCalc);

	// Function to reset all state variables
	const handleReset = () => {
		setClipBoardRes([]);
		setClipArray([]);
		setLoading(false);
		setMarkupRap("");
		setMarkupPerPriceCt("");
		setMarkupDolPriceCt("");
		setMarkupPerValue("");
		setMarkupRapDes(null);
		setMarkupPerPriceCtDes(null);
		setMarkupDolPriceCtDes(null);
		setMarkupPerValueDes(null);
		setFinalCalc([]);
	};

	// Modified onClose handler to reset state
	const handleModalClose = () => {
		handleReset(); // Reset all state
		onClose(); // Call the parent's onClose if provided
	};

	// A common change handler to ensure mutual exclusivity
	const handleInputChange = (key, value) => {
		setMarkupRap(key === "markupRap" ? value : "");
		setMarkupPerPriceCt(key === "markupPerPriceCt" ? value : "");
		setMarkupDolPriceCt(key === "markupDolPriceCt" ? value : "");
		setMarkupPerValue(key === "markupPerValue" ? value : "");
	};

	const handleIncreasePriceCopy = async (event) => {
		// Prevent the default behavior
		event.preventDefault();

		// Stop the event from propagating to parent handlers
		event.stopPropagation();

		const processedChecked = checked;

		if (!processedChecked.length) {
			OpenNotification({
				type: "error",
				title: "Please select a stone.",
			});
			return;
		}

		setLoading(true);
		let obj = {
			...IncreasePriceAPIs,
			request: {
				vStnId: processedChecked.map((item) => item.vStnId),
				...(markupRap ? { MarkupRap: markupRap } : {}),
				...(markupPerPriceCt ? { MarkupPerPrCrt: markupPerPriceCt } : {}),
				...(markupDolPriceCt ? { MarkupDolrPrCrt: markupDolPriceCt } : {}),
				...(markupPerValue ? { MarkupDolrVal: markupPerValue } : {}),
			},
		};
		UtilService.callApi(obj, function (err, res) {
			if (err && err.data.code === "E_BAD_REQUEST") {
				return OpenNotification({
					type: "error",
					title: err.data.message,
				});
			} else if (res && res.code === "OK") {
				setFinalCalc(res.data?.diamonds);
				setMarkupRapDes(res.data?.markupData?.markupRap);
				setMarkupPerPriceCtDes(res.data?.markupData?.markupPerPrCrt);
				setMarkupDolPriceCtDes(res.data?.markupData?.markupDolrPrCrt);
				setMarkupPerValueDes(res.data?.markupData?.markupDolrVal);
			}
		});

		await Promise.all(processedChecked.map((item) => fetchWhatsAppContents(item, setLoading, setClipBoardRes)));
		OpenNotification({
			type: "success",
			title: "Stone details have been copied. Press Ctrl + V on your WhatsApp Web to send in a chat!",
		});
	};

	const isButtonDisabled = !(markupRap || markupPerPriceCt || markupDolPriceCt || markupPerValue);

	return (
		<CommonModal
			title="Share Stone"
			toggle={handleOk}
			visible={true}
			closable={true}
			handleOk={handleOk}
			handleCancel={handleModalClose}
		>
			{loading ? <Loader loading={true} fixed /> : null}
			<div className="detailShow d-flex flex-wrap justify-content-evenly">
				<div>
					<InputNumber
						label="Markup Rap"
						placeholder="Markup Rap"
						name="markupRap"
						min={0}
						max={99}
						value={markupRap}
						onChange={(value) => handleInputChange("markupRap", value)}
					/>
					<b style={{ color: "red", margin: "0 10px" }}>{markupRapDes}</b>
				</div>
				<div>
					<InputNumber
						label="Markup %(Price/ct)"
						placeholder="Markup %(Price/ct)"
						name="markupPerPriceCt"
						min={0}
						max={99}
						value={markupPerPriceCt}
						onChange={(value) =>
							handleInputChange("markupPerPriceCt", value)
						}
					/>

					<b style={{ color: "red", margin: "0 10px" }}>
						{markupPerPriceCtDes}
					</b>
				</div>
				<div>
					<InputNumber
						label="Markup $(Price/ct)"
						placeholder="Markup $(Price/ct)"
						name="markupDolPriceCt"
						min={0}
						max={999}
						value={markupDolPriceCt}
						onChange={(value) =>
							handleInputChange("markupDolPriceCt", value)
						}
					/>
					<b style={{ color: "red", margin: "0 10px" }}>
						{markupDolPriceCtDes}
					</b>
				</div>
				<div>
					<InputNumber
						label="Markup %(Value)"
						placeholder="Markup %(Value)"
						name="markupPerValue"
						min={0}
						max={999}
						value={markupPerValue}
						onChange={(value) =>
							handleInputChange("markupPerValue", value)
						}
					/>
					<b style={{ color: "red", margin: "0 10px" }}>
						{markupPerValueDes}
					</b>
				</div>
			</div>
			<div style={{ textAlign: "center" }}>
				<button className="commonButton" onClick={(event) => handleIncreasePriceCopy(event)} disabled={isButtonDisabled}>
					Copy
				</button>
			</div>
		</CommonModal>
	);
}

export default IncreasePrice;
